import { objectSpread2 as _objectSpread2 } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import PendoDivider from '../../../components/divider/pendo-divider.vue.js';
import PendoTable from '../../../components/table/pendo-table.vue.js';
import PendoAppCell from '../../../components/table/cell-types/pendo-app-cell.vue.js';
import PendoAppDisplay from '../../app-display/pendo-app-display.vue.js';
import PendoCheckbox from '../../../components/checkbox/pendo-checkbox.vue.js';
import PendoButton from '../../../components/button/pendo-button.vue.js';
import PendoModal from '../../../components/modal/pendo-modal.vue.js';
import PendoNotification from '../../../components/notification/pendo-notification.js';
import PendoActionsCell from '../../../components/table/cell-types/pendo-actions-cell.vue.js';
import PendoStatusOverlay from '../../status-overlay/pendo-status-overlay.vue.js';
import GuideAlertsStatus from './guide-alerts-status.vue.js';
import moment from 'moment';
import get from 'lodash/get.js';
import clone from 'lodash/clone.js';
import compact from 'lodash/compact.js';
import debounce from 'lodash/debounce.js';
import uniq from 'lodash/uniq.js';
import capitalize from 'lodash/capitalize.js';

function _await(value, then, direct) {
  if (direct) {
    return then ? then(value) : value;
  }
  if (!value || !value.then) {
    value = Promise.resolve(value);
  }
  return then ? value.then(then) : value;
}
function _async(f) {
  return function () {
    for (var args = [], i = 0; i < arguments.length; i++) {
      args[i] = arguments[i];
    }
    try {
      return Promise.resolve(f.apply(this, args));
    } catch (e) {
      return Promise.reject(e);
    }
  };
}
var script = {
  name: 'GuideAlertsTable',
  components: {
    PendoDivider,
    PendoTable,
    PendoAppCell,
    PendoAppDisplay,
    PendoCheckbox,
    PendoButton,
    PendoModal,
    PendoActionsCell,
    PendoStatusOverlay,
    GuideAlertsStatus
  },
  props: {
    errorData: {
      type: Array,
      required: true
    },
    guideAlertsAggStatus: {
      type: String,
      required: true
    },
    segmentsMap: {
      type: Object,
      required: true
    },
    filters: {
      type: Array,
      default: () => []
    },
    hasAdminAccess: {
      type: Boolean,
      default: false
    },
    tableConfig: {
      type: Object,
      default: () => {
        return {
          guideErrorsColumns: {},
          guideErrorsWidths: {},
          tableToggles: {}
        };
      }
    },
    productType: {
      type: String,
      default: 'engage'
    }
  },
  data() {
    return {
      isGroupedByGuide: true,
      showMutedAlerts: false,
      activeAlertModal: {
        isVisible: false,
        alert: null,
        modalMessage: '',
        type: null,
        loading: false,
        title: ''
      },
      initialColumns: ['impactedStep.name', 'errorType', 'alertStatus', 'visitorsImpacted', 'percentVisitorsImpacted', 'firstOccurrence', 'lastOccurrence', 'actions'],
      columns: [],
      columnWidthsConfig: undefined,
      mutedFilter: [],
      isUpdating: false,
      loadingText: 'Loading...',
      emptyText: 'There are no alerts found matching the current filters.',
      emptySubtext: 'Consider adjusting any applied filters. Any alerts on your guides will be seen here with information on impact and how it could potentially be fixed.'
    };
  },
  computed: {
    mutedAlerts() {
      return this.errorData.filter(alert => alert.muted).length;
    },
    mutedAlertText() {
      return `Show Muted Alerts (${this.mutedAlerts})`;
    },
    confirmButtonConfig() {
      return {
        loading: this.activeAlertModal.loading
      };
    },
    defaultColumns() {
      const columnHeaders = clone(this.initialColumns);
      if (!this.isGroupedByGuide) {
        columnHeaders.splice(1, 0, 'guideName');
      }
      return columnHeaders;
    },
    availableColumns() {
      const options = [{
        prop: 'impactedStep.name',
        type: 'link',
        label: 'Impacted Step',
        formatter: row => {
          const name = get(row.steps[row.impactedStepIndex], 'name', false);
          if (name) {
            return `${name}`;
          }
          return `Step ${row.impactedStepIndex + 1}`;
        },
        sortable: true
      }, {
        label: 'Alert Status',
        prop: 'alertStatus',
        width: 150,
        sortable: true
      }, {
        prop: 'visitorsImpacted',
        label: 'Visitors Impacted',
        sortable: true
      }, {
        prop: 'totalVisitors',
        label: 'Total Visitors',
        sortable: true
      }, {
        prop: 'percentVisitorsImpacted',
        label: 'Alert Rate',
        sortable: true,
        formatter: row => `${row.percentVisitorsImpacted}%`
      }, {
        prop: 'errorType',
        label: 'Alert Type',
        sortable: true,
        minWidth: 200
      }, {
        prop: 'lastUpdatedByUser',
        label: 'Last Updated By',
        sortable: true
      }, {
        prop: 'lastUpdatedAt',
        label: 'Last Updated Time',
        formatter: row => moment(row.lastUpdatedAt).format('MMM D, YYYY, h:mm:ss a'),
        width: 200,
        sortable: true
      }, {
        prop: 'launchMethodText',
        label: 'Activation Method',
        sortable: true
      }, {
        prop: 'appId',
        slot: 'app',
        label: 'App',
        minWidth: 160,
        sortable: true,
        sortBy: row => row.app.displayName.toLowerCase()
      }, {
        prop: 'segment',
        label: 'Segment',
        minWidth: 200,
        sortable: true,
        sortBy: row => row.segment.name.toLowerCase()
      }, {
        prop: 'firstOccurrence',
        label: 'First Alert Date',
        minWidth: 200,
        sortable: true,
        formatter: row => moment(row.firstOccurrence).format('MMM D, YYYY, h:mm:ss a')
      }, {
        prop: 'lastOccurrence',
        label: 'Last Alert Date',
        minWidth: 200,
        sortable: true,
        formatter: row => moment(row.lastOccurrence).format('MMM D, YYYY, h:mm:ss a')
      }, {
        prop: 'actions',
        type: 'actions',
        width: 80
      }];
      if (!this.isGroupedByGuide) {
        options.push({
          prop: 'guideName',
          label: 'Guide Name',
          sortable: true
        });
      }
      return options;
    },
    tableToggleState() {
      return `${this.showMutedAlerts}|${this.isGroupedByGuide}`;
    },
    processedData() {
      return this.errorData.reduce((acc, guide) => {
        const {
          impactedStepId,
          displayAlertType,
          steps
        } = guide;
        const stepIndex = this.findStepIndex(steps, impactedStepId);
        if (stepIndex > -1) {
          const segment = this.getSegmentInfo(guide);
          const errorType = this.getErrorType([displayAlertType]);
          const impactedStepIndex = stepIndex;
          acc.push(_objectSpread2(_objectSpread2({}, guide), {}, {
            segment,
            errorType,
            impactedStepIndex
          }));
        }
        return acc;
      }, []);
    },
    tableFilters() {
      return [...this.filters, ...this.mutedFilter];
    }
  },
  watch: {
    tableToggleState: debounce(_async(function (toggleState) {
      const _this = this;
      const [showMutedAlerts, isGroupedByGuide] = toggleState.split('|');
      const values = {
        showMutedAlerts: showMutedAlerts === 'true',
        isGroupedByGuide: isGroupedByGuide === 'true'
      };
      _this.updateUserSettings('displayAlertTableToggles', values);
      return _await();
    }), 500),
    defaultColumns() {
      this.processColumns(this.columnWidthsConfig);
    },
    showMutedAlerts: {
      immediate: true,
      handler() {
        this.mutedFilter = [{
          filterFn: row => this.showMutedAlerts || !row.muted
        }];
      }
    }
  },
  created() {
    this.getTableColumns();
    this.getTableToggles();
  },
  methods: {
    getTableColumns() {
      const {
        guideErrorsColumns,
        guideErrorsWidths
      } = this.tableConfig;
      if (get(guideErrorsWidths, 'value', false)) {
        this.columnWidthsConfig = guideErrorsWidths.value;
      }
      if (get(guideErrorsColumns, 'value', false)) {
        const columnConfig = guideErrorsColumns.value;
        if (columnConfig.indexOf('actions') < 0) {
          columnConfig.push('actions');
        }
        const expectedFirstColumn = this.availableColumns[0].prop;
        if (columnConfig[0] !== expectedFirstColumn) {
          const indexOfFirstColumn = columnConfig.indexOf(expectedFirstColumn);
          if (indexOfFirstColumn < 0) {
            columnConfig.unshift(expectedFirstColumn);
          } else if (indexOfFirstColumn > 0) {
            columnConfig[indexOfFirstColumn] = columnConfig[0];
            columnConfig[0] = expectedFirstColumn;
          }
        }
        this.initialColumns = columnConfig;
      } else {
        this.processColumns(this.columnWidthsConfig);
      }
    },
    getTableToggles() {
      const toggles = get(this.tableConfig, 'tableToggles.value', {});
      this.showMutedAlerts = get(toggles, 'showMutedAlerts', false);
      this.isGroupedByGuide = get(toggles, 'isGroupedByGuide', true);
    },
    onLinkClick(_ref) {
      let {
        row
      } = _ref;
      this.$emit('step-link-click', row.guideId);
    },
    confirmModal() {
      this.activeAlertModal.loading = true;
      const {
        alert,
        type
      } = this.activeAlertModal;
      if (type === 'resolve') {
        alert.alertStatus = 'Resolved';
      }
      if (type === 'mute') {
        alert.muted = true;
      }
      this.updateAlertRow({
        activeAlert: alert,
        isVisible: true
      });
    },
    isMuted(_ref2) {
      let {
        row
      } = _ref2;
      if (row.muted) {
        return 'muted';
      }
    },
    cancelModal() {
      if (this.activeAlertModal.type === 'resolve') {
        this.$refs[`${this.activeAlertModal.alert.childKey}:alertStatus`].setStatus();
      }
      this.resetActiveAlertModal();
    },
    showUsers(row) {
      const {
        visitorsImpacted: users,
        guideId,
        impactedStepId: guideStepId,
        displayAlertType: guideShownReason
      } = row;
      this.$emit('show-panel-users', {
        users,
        guideId,
        guideStepId,
        guideShownReason
      });
    },
    showNotes(alert) {
      this.$emit('show-panel-notes', alert);
    },
    processColumns(columnWidthsConfig) {
      this.columns = compact(this.availableColumns.reduce((columns, column) => {
        const index = this.defaultColumns.indexOf(column.prop);
        if (index >= 0) {
          column.visible = true;
          columns[index] = column;
        } else {
          column.visible = undefined;
          columns.push(column);
        }
        if (columnWidthsConfig) {
          const savedColumnWidth = get(columnWidthsConfig, `['${column.prop}']`);
          if (savedColumnWidth) {
            column.width = savedColumnWidth;
          }
        }
        return columns;
      }, new Array(this.defaultColumns.length)));
    },
    changeColumns(columns) {
      this.initialColumns = columns.columns;
      this.updateUserSettings('guideErrorsColumns', this.initialColumns);
    },
    resizeColumn(column) {
      const columnWidthsConfig = this.columns.reduce((config, localColumn) => {
        const newWidth = column.column === localColumn.prop ? column.width : localColumn.width;
        config[localColumn.prop] = newWidth;
        localColumn.width = newWidth;
        return config;
      }, {});
      this.columnWidthsConfig = columnWidthsConfig;
      this.updateUserSettings('guideErrorsWidths', columnWidthsConfig);
    },
    muteModalMessage(row) {
      let stepname = get(row.steps[row.impactedStepIndex], 'name', false);
      if (!stepname) {
        stepname = `Step ${row.impactedStepIndex + 1}`;
      }
      return `"${row.guideName}'s step: ${stepname}, type: ${row.errorType}" will remain muted until it is unmuted.`;
    },
    onShowActiveAlertMuteModal(alert) {
      this.activeAlertModal = {
        loading: false,
        title: 'Mute this Alert?',
        alert,
        modalMessage: this.muteModalMessage(alert),
        isVisible: true,
        type: 'mute'
      };
    },
    unmute(alert) {
      alert.muted = false;
      this.updateAlertRow({
        activeAlert: alert
      });
    },
    updateAlertRow(_ref3) {
      let {
        activeAlert,
        isVisible = false
      } = _ref3;
      this.isUpdating = true;
      const {
        alertStatus,
        muted,
        appId,
        guideId,
        impactedStepId,
        displayAlertType
      } = activeAlert;
      const alert = {
        status: alertStatus,
        muted,
        appId,
        guideId,
        guideStepId: impactedStepId,
        guideDisplayAlertType: displayAlertType
      };
      if (!activeAlert.hasAlertCreated) {
        return this.$emit('create-display-alert', {
          alert,
          activeAlert,
          isVisible
        });
      }
      this.$emit('update-display-alert', {
        alert,
        activeAlert,
        isVisible
      });
    },
    informError() {
      let closeModal = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
      this.isUpdating = false;
      if (closeModal) {
        this.resetActiveAlertModal();
      }
      return PendoNotification({
        type: 'error',
        title: 'Alert Not Updated',
        message: 'Looks like something went wrong. Please refresh and try again.',
        duration: 3000
      });
    },
    handleAlertStatusUpdate(alert, status) {
      if (status === 'Resolved') {
        this.activeAlertModal = {
          alert,
          isVisible: true,
          title: 'Resolve this Alert?',
          modalMessage: 'This guide step will remain resolved until another alert is triggered.',
          type: 'resolve',
          loading: false
        };
        return;
      }
      alert.alertStatus = status;
      this.updateAlertRow({
        activeAlert: alert
      });
    },
    getNoteCount(row) {
      const numberOfNotes = get(row, 'notes', []).length;
      return numberOfNotes || '';
    },
    resetActiveAlertModal() {
      this.activeAlertModal = {
        alert: null,
        type: null,
        title: '',
        modalMessage: '',
        isVisible: false,
        loading: false
      };
    },
    updateTableRow(activeAlert) {
      let closeModal = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
      this.$refs.pendoTable.updateRow(activeAlert);
      this.isUpdating = false;
      if (closeModal) {
        this.resetActiveAlertModal();
      }
    },
    updateUserSettings(name, value) {
      const settings = {
        name,
        namespaced: true,
        value,
        force: true
      };
      this.$emit('update-user-settings', settings);
    },
    tableDataChange(data) {
      this.$emit('table-data-change', data);
    },
    getErrorType(errors) {
      const realTimeoutReasons = ['page', 'element', 'other'];
      const uniqueReasons = uniq(errors);
      if (uniqueReasons.length === 1 && uniqueReasons[0] === '') {
        return 'Other';
      }
      let reason;
      let reasons = '';
      if (uniqueReasons[0] === '') {
        uniqueReasons.shift();
      }
      uniqueReasons.forEach((timeoutReason, i) => {
        if (!realTimeoutReasons.includes(timeoutReason)) {
          reason = '';
        } else if (timeoutReason === 'page') {
          reason = 'Page Mismatch';
          reasons += `${i > 0 ? ', ' : ''}${reason}`;
        } else if (timeoutReason === 'element') {
          reason = 'Element Missing';
          reasons += `${i > 0 ? ', ' : ''}${reason}`;
        } else if (timeoutReason === 'other') {
          reason = 'Other';
          reasons += `${i > 0 ? ', ' : ''}${reason}`;
        }
      });
      if (!reasons.length) {
        reasons = 'Other';
      }
      return reasons;
    },
    getSegmentInfo(guide) {
      if (!guide.audienceFilter || guide.audienceFilter.length === 0) {
        return {
          name: 'Everyone'
        };
      }
      const isCustomNestedSegment = this.filtersHaveSegmentRule(guide.audienceFilter);
      const segmentId = get(guide.audienceFilter[0], 'segmentId');
      if (!isCustomNestedSegment && segmentId) {
        const segment = this.segmentsMap[segmentId];
        if (get(segment, 'name', '') === 'Everyone' && get(segment, 'aside', false)) {
          segment.label = `${segment.name} ${segment.aside}`;
        }
        return segment;
      }
      return {
        name: 'Custom Segment'
      };
    },
    findStepIndex(steps, stepId) {
      if (!steps) {
        return -1;
      }
      if (Array.isArray(steps)) {
        return steps.findIndex(step => step.id === stepId);
      }
      return steps.id === stepId ? 0 : -1;
    },
    filtersHaveSegmentRule() {
      let filters = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
      return filters.some(filter => filter.or ? filter.or.some(rule => rule.kind === 'segment') : filter.kind === 'segment');
    },
    mutedActionTooltip(row) {
      const action = row.muted ? 'unmute' : 'mute';
      if (!row.canChangeAlert) {
        return `You do not have sufficient permissions for ${row.app.displayName} to ${action} the alert`;
      }
      return capitalize(action);
    }
  }
};

export { script as default };
//# sourceMappingURL=guide-alerts-table.vue2.js.map
